import { Link } from 'react-router-dom';
import React, { useRef } from 'react';
import { ITestIssueSchema } from '@/api/testIssue';
import { CardAttachment } from './CardAttachment';
import moment from 'moment';
import { CardInformationList } from './CardInformationList';
import ListLinkedTestCaseModal from './modal/ListLinkedTestCaseModal';
import { CardDetail } from '@/components/shared/card/CardDetail';
import {
  CommentHistory,
  CommentsHistoryRef,
} from '@/pages/comment/CommentHistory';
import StatusHistoryModal from './modal/StatusHistoryModal';
import AddCommentForm from '@/components/comment/AddCommentForm';

type TestIssueDetailProps = {
  testIssueData: ITestIssueSchema;
  isPublic?: boolean;
};

export const TestIssueDetail: React.FC<TestIssueDetailProps> = ({
  testIssueData,
  isPublic,
}) => {
  const ref = useRef<CommentsHistoryRef>(null);

  return (
    <div className="flex gap-4">
      <div className="flex flex-col gap-4 basis-3/4">
        <div className="flex flex-row gap-6">
          <CardDetail title="Status" value={testIssueData.status} />
          <CardDetail title="Priority" value={testIssueData.priority} />
        </div>
        <div className="flex flex-row gap-6">
          <CardDetail title="Category" value={testIssueData.category} />
          <CardDetail
            title="Step to reproduce"
            value={testIssueData.step_issue?.split(/\r?\n/)}
          />
        </div>
        <div className="flex flex-row gap-6">
          <CardDetail title="Severity" value={testIssueData.severity} />
          <CardDetail title="Issue Type" value={testIssueData.issue_type} />
        </div>
        <div className="flex flex-row gap-6">
          <CardDetail
            title="Environment"
            value={testIssueData.environment_issue}
          />
          <CardDetail
            title="Assigned To"
            value={testIssueData?.assignee?.full_name}
          />
        </div>
        <CardDetail title="Description" value={testIssueData.description} />

        <CommentHistory ref={ref} testIssue={testIssueData} />

        <CardAttachment title="Attachment" media={testIssueData.medias ?? []} />
      </div>
      <div className="flex flex-col gap-4 basis-1/4">
        <div className="flex flex-col gap-2 p-4 border rounded-md">
          <span className="font-semibold">Information</span>
          <CardInformationList
            title="Created By"
            value={testIssueData.created_by?.full_name}
          />
          <CardInformationList
            title="Created Date"
            value={moment(testIssueData.created_at).format('DD MMMM YYYY')}
          />
          <CardInformationList
            title="Module"
            value={testIssueData.module?.module_name ?? '-'}
          />
          <CardInformationList
            title="Release"
            value={testIssueData.release?.name ?? '-'}
          />

          {testIssueData.test_run?.title ? (
            <Link
              to={`/project/${testIssueData.project_id}/test-run/${testIssueData.test_run_id}`}
              className="text-primary hover:underline font-medium"
            >
              <CardInformationList
                title="Test Run"
                value={testIssueData.test_run.title}
              />
            </Link>
          ) : (
            <CardInformationList title="Test Run" value="-" />
          )}
        </div>
        <div className="flex flex-col items-start gap-2 p-4 border rounded-md">
          <span className="font-semibold">Action</span>
          {!isPublic && (
            <>
              {/* Add Comment */}
              <AddCommentForm
                testIssue={testIssueData}
                onSuccess={() => {
                  ref.current?.refetch();
                }}
              />
            </>
          )}

          {/* History logs */}
          <StatusHistoryModal testIssueId={testIssueData.id!} />

          {!isPublic && (
            <ListLinkedTestCaseModal testIssueId={testIssueData.id!} />
          )}
        </div>
      </div>
    </div>
  );
};
